import React, { useEffect, useRef, useState } from "react";
import { firstUpperOnly } from "../../../shared/utils/stringUtilities";
import useBrowserMode from "../../../shared/hooks/useBrowserMode";
import {
    MenuItem,
    Stack,
    Typography,
    Avatar,
    Box,
    Divider,
    Slide,
    Modal,
    useMediaQuery,
    IconButton,
    List,
    Collapse
} from "@mui/material";
import { menuProfile } from "./queries/queryMenu";
import { navigate } from 'gatsby';

import {
    StyledMenu,
    MobileMenuContainer,
    MenuHeader,
    MobileMenuItem,
    MobileSubMenuItem
} from './styles';

import { LoginInterface, MenuItemMobile as MenuItemType, MenuSubItem, MenuItemOrDivider } from '../../../interfaces/HeaderMobile';

const transformContentfulData = (data: any): MenuItemOrDivider[] => {
    if (!data?.opciones?.[0]?.referenciaContenido) {
        return [];
    }

    const menuItems = data.opciones[0].referenciaContenido.map((item: any) => {
        const title = item.tituloBanner.trim();
        const hasSubmenu = !!item.referenciaContenido?.length;

        // Crear el item del menú
        const menuItem: MenuItemType = {
            title,
            iconUrl: item.imagenPrincipal?.[0]?.file?.url ? `https:${item.imagenPrincipal[0].file.url}` : undefined,
            gtm: `gtmMenuTrans${title.replace(/\s/g, '')}`,
            hasSubmenu,
            redireccionBoton: item.redireccionBoton,
            className: title === 'Cerrar sesión' ? 'logout-item' : ''
        };

        // Si tiene subitems, agregarlos desde referenciaContenido
        if (hasSubmenu && item.referenciaContenido) {
            menuItem.submenu = item.referenciaContenido.map((subItem: any) => ({
                title: subItem.tituloBanner.trim(),
                redireccionBoton: subItem.redireccionBoton
            }));
        }

        return menuItem;
    });

    // Agregar dividers después de Conexión laboral y Configuración
    return menuItems.reduce((acc: MenuItemOrDivider[], item: MenuItemType, index: number) => {
        acc.push(item);
        if (item.title === 'Conexión laboral' || item.title === 'Configuración') {
            acc.push({ divider: true } as MenuItemOrDivider);
        }
        return acc;
    }, []);
};

const transformUserName = (userName: string) => {
   if(!userName || typeof userName !== 'string') return '';

   const isLargeName = userName.length > 12;

   return isLargeName ? userName.slice(0, 12) + '...' : userName;

}

const Login = (props: LoginInterface) => {
    const { setIsOpenModal } = props;
    const { document, window } = useBrowserMode();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
    const [mobileExpandedItems, setMobileExpandedItems] = useState<{ [key: string]: boolean }>({});
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const gigyaAccount = props.gigyaAccount;
    const profileData = menuProfile();
    const firstMenuItemRef = useRef<HTMLLIElement>(null);
    const isMobile = useMediaQuery('(max-width:768px)');
    const isTablet = useMediaQuery('(max-width:1280px)');

    const menuItems = transformContentfulData(profileData);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (isMobile) {
            setMobileMenuOpen(true);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setExpandedMenu(null);
    };

    const handleCloseMobileMenu = () => {
        setMobileMenuOpen(false);
        setMobileExpandedItems({});
    };

    const handleMenuItemClick = (item: MenuItemType) => {
        if (item.hasSubmenu) {
            setExpandedMenu(expandedMenu === item.title ? null : item.title);
        } else if (item.title === 'Cerrar sesión') {
            props.logout();
            handleClose();
            localStorage.removeItem("uid-pre");
        } else if (item.title === 'Ir a mi cuenta Comfama') {
            props.updateProfile();
            handleClose();
        } else if (item.title === 'Perfil personas') {
            navigate('/personas/perfil-registro');
            handleClose();
        } else if (item.redireccionBoton) {
            window.location.href = `/${item.redireccionBoton}`;
            handleClose();
        } else {
            handleClose();
        }
    };

    const handleMobileMenuItemClick = (item: MenuItemType) => {
        if (item.hasSubmenu) {
            setMobileExpandedItems(prev => ({
                ...prev,
                [item.title]: !prev[item.title]
            }));
        } else if (item.title === 'Cerrar sesión') {
            props.logout();
            handleCloseMobileMenu();
            localStorage.removeItem("uid-pre");
        } else if (item.title === 'Ir a mi cuenta Comfama') {
            props.updateProfile();
            handleCloseMobileMenu();
        } else if (item.title === 'Perfil personas') {
            navigate('/personas/perfil');
            handleCloseMobileMenu();
        } else if (item.redireccionBoton) {
            window.location.href = `/${item.redireccionBoton}`;
            handleCloseMobileMenu();
        } else {
            handleCloseMobileMenu();
        }
    };

    const handleMobileSubmenuItemClick = (subItem: MenuSubItem, parentTitle: string, parentIndex: number, subItemIndex: number) => {
        if (parentTitle === "Perfil laboral") {
            sessionStorage.setItem('temp_indexMenu', parentIndex.toString());
            sessionStorage.setItem('temp_subIndexMenu', subItemIndex.toString());
            navigate('/personas/perfil-registro');
        } else if (subItem.redireccionBoton) {
            navigate(`/${subItem.redireccionBoton}`);
        }
        handleCloseMobileMenu();
    };

    useEffect(() => {
            const handleEscape = (event: KeyboardEvent) => {
                if (event.key === "Escape") {
                    console.log("Escape detectado! Cerrando menú...");
                    handleClose();

                    if (mobileMenuOpen) {
                        handleCloseMobileMenu();
                    }
                }
            };

            document.addEventListener("keydown", handleEscape);
            return () => {
                document.removeEventListener("keydown", handleEscape);
            };
    }, [anchorEl, mobileMenuOpen]);

    const getInitials = () => {
        return `${gigyaAccount?.profile?.firstName?.charAt(0) || ''}${gigyaAccount?.profile?.lastName?.charAt(0) || ''}`;
    };

    const userLoginAdvice = () => {
        const checkGuideCdc = localStorage.getItem("guideCdc");
        if (checkGuideCdc !== "true") {
            setIsOpenModal(true);
        } else {
            props.loginComfamaId();
        }
    }

    // Mobile Menu Component
    const MobileMenu = () => (
        <MobileMenuContainer>
            <MenuHeader>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#303030 !important' }} aria-hidden="true">
                    Hola, {gigyaAccount?.profile?.firstName ? firstUpperOnly(transformUserName(gigyaAccount.profile.firstName)) : 'Yhennyfer'}
                </Typography>
                <IconButton
                    onClick={handleCloseMobileMenu}
                    aria-label="Cerrar menú"
                    sx={{ color: '#303030 !important'}}
                >
                    <i className="icon icon-close" style={{ fontSize: '24px'}}></i>
                </IconButton>
            </MenuHeader>

            <List sx={{ padding: 0 }} role="menu" aria-label="Menú de usuario">
                {menuItems.map((item, index) => (
                    'divider' in item ? (
                        <Divider key={`divider-${index}`} sx={{ my: 1 }} aria-hidden="true" />
                    ) : (
                        <div key={item.title}>
                            <MobileMenuItem
                                onClick={() => handleMobileMenuItemClick(item)}
                                sx={{
                                    color: mobileExpandedItems[item.title] ? '#ff0066' : 'inherit'
                                }}
                                className={item.gtm}
                                role="menuitem"
                                aria-label={item.title + (item.hasSubmenu ? ' - Pulse para " + (mobileExpandedItems[item.title] ? "contraer" : "expandir") + " el submenú' : '')}
                                aria-expanded={item.hasSubmenu ? mobileExpandedItems[item.title] : undefined}
                                aria-controls={item.hasSubmenu ? `submenu-${item.title.replace(/\s/g, '-')}` : undefined}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    {item.iconUrl && (
                                        <img
                                            src={item.iconUrl}
                                            alt=""
                                            aria-hidden="true"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                marginRight: '16px',
                                                opacity: item.className === 'logout-item' ? 0.7 : 1
                                            }}
                                        />
                                    )}
                                    <Typography sx={{ flex: 1, color: '#303030 !important', }}>
                                        {item.title}
                                    </Typography>
                                    {item.hasSubmenu && (
                                        <i
                                            className={`icon ${mobileExpandedItems[item.title] ? 'icon-chevron-down' : 'icon-chevron-up'}`}
                                            style={{ fontSize: '16px' }}
                                            aria-hidden="true"
                                        ></i>
                                    )}
                                </Box>
                            </MobileMenuItem>

                            {item.hasSubmenu && item.submenu && (
                                <Collapse
                                    in={mobileExpandedItems[item.title]}
                                    timeout="auto"
                                    unmountOnExit
                                    id={`submenu-${item.title.replace(/\s/g, '-')}`}
                                >
                                    <List disablePadding role="menu" aria-label={`Submenú de ${item.title}`}>
                                        {item.submenu.map((subItem, subIndex) => (
                                            <MobileSubMenuItem
                                                key={subItem.title}
                                                onClick={() => handleMobileSubmenuItemClick(subItem, item.title, index, subIndex)}
                                                role="menuitem"
                                                aria-label={subItem.title}
                                            >
                                                <Typography>{subItem.title}</Typography>
                                            </MobileSubMenuItem>
                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </div>
                    )
                ))}
            </List>
        </MobileMenuContainer>
    );

    return (
        <Stack direction="row" alignItems="center" spacing={2}>
            {gigyaAccount ? (
                <div>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar
                                src={gigyaAccount.profile?.thumbnailURL}
                                alt=""
                                aria-hidden="true"
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: '#ff0066',
                                    cursor: 'pointer'
                                }}
                                onClick={handleClick}
                            >
                                {!gigyaAccount.profile?.thumbnailURL && getInitials()}
                            </Avatar>
                            <Box
                                onClick={handleClick}
                                sx={{
                                    position: 'absolute',
                                    bottom: -2,
                                    right: -2,
                                    width: '18px',
                                    height: '18px',
                                    backgroundColor: 'white',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    zIndex: 1
                                }}
                            >
                                <button
                                    id="menu-button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (isMobile) {
                                            setMobileMenuOpen(!mobileMenuOpen);
                                        } else {
                                            setAnchorEl(anchorEl ? null : event.currentTarget);
                                        }
                                    }}
                                    aria-expanded={Boolean(anchorEl) || mobileMenuOpen}
                                    aria-controls={Boolean(anchorEl) ? "menu" : undefined}
                                    aria-label={Boolean(anchorEl) || mobileMenuOpen ? "Cerrar menú de perfil" : "Abrir menú de perfil"}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <i
                                        className={`icon ${Boolean(anchorEl) || mobileMenuOpen ? "icon-caret-up" : "icon-caret-down"}`}
                                        style={{
                                            fontSize: '10px',
                                            color: '#ff0066'
                                        }}
                                        aria-hidden="true"
                                    />
                                </button>
                            </Box>
                        </Box>
                   {!isTablet && (
                      <Typography
                         title={gigyaAccount.profile?.firstName}
                         variant="body2"
                         sx={{
                            mt: 1,
                            maxWidth: 140,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                         }}
                         aria-hidden="true">
                         ¡Hola, {firstUpperOnly(transformUserName(gigyaAccount.profile.firstName))}!
                      </Typography>
                   )}
                    </Box>

                    {/* Desktop Menu */}
                    {!isMobile && (
                        <StyledMenu
                            id="menu"
                            role="menu"
                            aria-label="Menú de usuario"
                            anchorEl={anchorEl}
                            disablePortal
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                            aria-labelledby="menu-button"
                            onKeyDown={(event) => {
                                if (event.key === "Escape") {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setExpandedMenu(null);
                                    handleClose();
                                }
                            }}
                        >
                            {menuItems.map((item: MenuItemOrDivider, index: number) => (
                                'divider' in item ? (
                                    <Divider key={`divider-${index}`} aria-hidden="true" />
                                ) : (
                                    <div key={item.title}>
                                        <MenuItem
                                            onKeyDown={(event) => {
                                                if (event.key === "Escape") {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setExpandedMenu(null);
                                                    handleClose();
                                                }
                                            }}
                                            ref={index === 0 ? firstMenuItemRef : null}
                                            tabIndex={0}
                                            key={index} 
                                            role="menuitem"
                                            aria-label={item.title + (item.hasSubmenu ? ' - Pulse para ' + (expandedMenu === item.title ? 'contraer' : 'expandir') + ' el submenú' : '')}
                                            aria-expanded={item.hasSubmenu ? expandedMenu === item.title : undefined}
                                            aria-controls={item.hasSubmenu ? `submenu-${item.title.replace(/\s/g, '-')}` : undefined}
                                            sx={{
                                                padding: '12px'
                                            }}
                                            onClick={() => handleMenuItemClick(item)}
                                            className={`${item.gtm} ${item.className || ''} ${expandedMenu === item.title ? 'active' : ''}`}
                                        >
                                            {item.iconUrl && (
                                                <img src={item.iconUrl} alt="" aria-hidden="true" />
                                            )}
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                {item.title}
                                                {item.hasSubmenu && (
                                                    <i
                                                        className={`icon ${expandedMenu === item.title ? "icon-chevron-down" : "icon-chevron-up"}`}
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </Box>
                                        </MenuItem>
                                        {item.hasSubmenu && expandedMenu === item.title && item.submenu && (
                                            <Box id={`submenu-${item.title.replace(/\s/g, '-')}`} role="menu" aria-label={`Submenú de ${item.title}`}>
                                                {item.submenu.map((subItem: MenuSubItem, indexSubMenu: number) => (
                                                    <MenuItem
                                                        key={indexSubMenu}
                                                        role="menuitem"
                                                        aria-label={subItem.title}
                                                        onKeyDown={(event) => {
                                                            if (event.key === "Escape") {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                setExpandedMenu(null);
                                                                handleClose();
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            if (item.title === "Perfil laboral") {
                                                                sessionStorage.setItem('temp_indexMenu', index.toString());
                                                                sessionStorage.setItem('temp_subIndexMenu', indexSubMenu.toString());
                                                                navigate('/personas/perfil-registro');
                                                            } else {
                                                                navigate(`/${subItem.redireccionBoton}`);
                                                            }
                                                            handleClose();
                                                        }}
                                                        className="submenu-item"
                                                    >
                                                        {subItem.title}
                                                    </MenuItem>
                                                ))}
                                            </Box>
                                        )}
                                    </div>
                                )
                            ))}
                        </StyledMenu>
                    )}
                 {mobileMenuOpen && (
                    <Modal
                        open={mobileMenuOpen}
                        onClose={handleCloseMobileMenu}
                        aria-label="Menú de usuario móvil"
                        keepMounted
                        disablePortal
                        disableEnforceFocus={false}
                        disableAutoFocus={false}
                        aria-modal="true"
                        role="dialog"
                        sx={{
                            zIndex: 9999,
                        }}
                        slotProps={{
                            backdrop: {
                                style: {
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                }
                            }
                        }}
                    >
                        <Slide direction="left" in={mobileMenuOpen} mountOnEnter unmountOnExit>
                            <Box
                            tabIndex={-1}
                                sx={{
                                    position: 'fixed',
                                    top: 0,
                                    right: 0,
                                    width: '80%',
                                    maxWidth: '360px',
                                    height: '100vh',
                                    bgcolor: 'white',
                                    zIndex: 10000,
                                    outline: 'none',
                                    overflow: 'auto',
                                    boxSizing: 'border-box',
                                    boxShadow: '-4px 0 10px rgba(0,0,0,0.1)',
                                }}
                            >
                                <MobileMenu />
                            </Box>
                        </Slide>
                    </Modal>
                 )}
                </div>
            ) : (
                <Typography
                    component="button"
                    className="gtmMenuTransIniciarSesion"
                    onClick={userLoginAdvice}
                    aria-label="Iniciar sesión"
                    sx={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'text.primary',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        "&:hover": {
                            color: "#ee2b7b",
                        },
                    }}
                >
                    Iniciar sesión
                </Typography>
            )}
        </Stack>
    );
};

export default Login;
